import { UserProfile } from '@/models/dexie/UserProfile';
import { UserProfileStore } from '@/store/store.userProfile';
import { ConfigurationService } from '@/services';
import { ApiIdentifers } from '@/config/index';

class UnitsHelperUtil {

    get getUserProfile(): UserProfile {
        return UserProfileStore.userProfile;
    }

    /**
     * Method to convert units from base unit to a given unit format
     *
     * @param inUnitIdentifier  Identifier string of the unit type
     * @param inRangeStep       Given steps for precision
     * @param inValue           The base value to convert
     * @param inUnit            Optional unit format. For most units, user profile overrides this.
     *                          Fineness never uses user profile — only the row/dataset unit.
     */
    public calcByUnit(
        inUnitIdentifier: string,
        inRangeStep: number = 0,
        inValue: number,
        inUnit?: number,
    ): string {
        const unit = ConfigurationService.unitsConfiguration.sections.find((sec) => sec.identifier === inUnitIdentifier);
        const isFineness = inUnitIdentifier === ApiIdentifers.unitFineness;
        const userProfileUnit = isFineness
            ? undefined
            : UserProfileStore.userProfile.units.find((upu) => upu.unitIdentifier === inUnitIdentifier);
        let value = 0;
        const usedUnit = userProfileUnit && userProfileUnit.selectedValue != null ? userProfileUnit.selectedValue : inUnit;
        if (unit !== undefined) {
            switch (unit.identifier) {
                case 'bfe36c97':
                    value = this.calcLinearDensity(usedUnit != null ? usedUnit : 3, inValue);
                    break;
                case '3f03d88e':
                    value = this.calcLinearDensity(usedUnit != null ? usedUnit : 3, inValue);
                    break;
                case '75bc99f4':
                    value = this.calcTemperature(usedUnit != null ? usedUnit : 0, inValue);
                    break;
                case '9877fa39':
                    // No profile: use dataset Fineness Unit only; if missing, leave value as stored
                    value = this.calcFineness(usedUnit != null ? usedUnit : 0, inValue);
                    break;
                case 'f3f76d28':
                    value = this.calcTwist(usedUnit != null ? usedUnit : 0, inValue);
                    break;
                case '52f59626':
                    value = this.calcAlpha(usedUnit != null ? usedUnit : 0, inValue);
                    break;
                case '2626943f':
                    value = this.calcLinearSpeed(usedUnit != null ? usedUnit : 0, inValue);
                    break;
                case 'd3d4dac1':
                    value = this.calcMassFlowRate(usedUnit != null ? usedUnit : 0, inValue);
                    break;
                case '5c25d62b':
                    value = this.calcLongitude(usedUnit != null ? usedUnit : 0, inValue);
                    break;
                case '09a9f8b2':
                    value = this.calcMass(usedUnit != null ? usedUnit : 0, inValue);
                    break;
                case 'fae69dbc':
                    value = this.calcStrengthType(usedUnit != null ? usedUnit : 0, inValue);
                    break;
                default:
                    value = inValue;
                    break;
            }
        }

        return value.toFixed(this.getPrecision(inRangeStep));
        // return value.toString();
    }

    /**
     * Method to get precision for value
     * @param minmax The value of the min or max field to calc precision
     */
    public getPrecision(minmax: number): number {
        let precision = 0;
        const numberStringArray: string[] = minmax.toString().split('.');
        if (numberStringArray.length > 1) {
            precision = numberStringArray[1].length;
        }

        return precision;
    }

    /** for conversion see:
     * - https://appsfactory.atlassian.net/wiki/spaces/TTDB/pages/1739686395/Units
     * - code in backend (BE and FE should use same values for conversions)
     * - https://appsfactory.atlassian.net/wiki/download/attachments/1545306749/Calculation%20sheet%20(2).xls?api=v2
     */

    /**
     * Method to convert from base unit (Ne) to other units
     *
     * @param inUnit    Id of selected unit format
     * @param inValue   Base-value to convert
     */
    private calcLinearDensity(inUnit: number, inValue: number): number {
        let value = 0;

        switch (inUnit) {
            /** tex */
            case 0:
                value = 590.5 / inValue;
                break;
            /** ktex */
            case 1:
                value = 590.5 / (inValue * 1000);
                break;
            /** Nm */
            case 2:
                value = 1.6934 * inValue;
                break;
            /** Ne */
            case 3:
                value = inValue;
                break;
            /** gr/yd */
            case 4:
                value = 8.33 / inValue;
                break;
            default:
                break;
        }


        return value;
    }

    /**
     * Method to convert from base unit (°C) to other units
     *
     * @param inUnit    Id of selected unit format
     * @param inValue   Base-value to convert
     */
    private calcTemperature(inUnit: number, inValue: number): number {
        let value = 0;
        switch (inUnit) {
            case 0:
                value = inValue;
                break;
            case 1:
                value = (inValue * (9 / 5)) + 32;
                break;
            default:
                break;
        }
        return value;
    }

    /**
     * Fineness: no conversion.
     * Backend stores the value as entered; FE returns the same for dtex/den/MIC/µm.
     * (Previously den used × 0.9 — removed to match BE.)
     */
    private calcFineness(inUnit: number, inValue: number): number {
        return inValue;
    }

    /**
     * Method to convert from base unit (T/m) to other units
     *
     * @param inUnit    Id of selected unit format
     * @param inValue   Base-value to convert
     */
    private calcTwist(inUnit: number, inValue: number): number {
        let value = 0;
        switch (inUnit) {
            case 0:
                value = inValue;
                break;
            case 1:
                value = inValue / 1.0936;
                break;
            default:
                break;
        }
        return value;
    }

    /**
     * Method to convert from base unit (αtex) to other units
     *
     * @param inUnit    Id of selected unit format
     * @param inValue   Base-value to convert
     */
    private calcAlpha(inUnit: number, inValue: number): number {
        let value = 0;
        switch (inUnit) {
            case 0:
                value = inValue;
                break;
            case 1:
                value = inValue / 31.6;
                break;
            case 2:
                value = inValue / 958;
                break;
            default:
                break;
        }
        return value;
    }

    /**
     * Method to convert from base unit (m/min) to other units
     *
     * @param inUnit    Id of selected unit format
     * @param inValue   Base-value to convert
     */
    private calcLinearSpeed(inUnit: number, inValue: number): number {
        let value = 0;
        switch (inUnit) {
            /** m/min */
            case 0:
                value = inValue;
                break;
            /** yd/min */
            case 1:
                value = (inValue * 1.10936);
                break;
            default:
                break;
        }
        return value;
    }

    /**
     * Method to convert from base unit (kg/h) to other units
     *
     * @param inUnit    Id of selected unit format
     * @param inValue   Base-value to convert
     */
    private calcMassFlowRate(inUnit: number, inValue: number): number {
        let value = 0;
        switch (inUnit) {
            /** kg/h */
            case 0:
                value = inValue;
                break;
            /** lbs/h */
            case 1:
                value = (inValue * 2.2046226218);
                break;
            default:
                break;
        }
        return value;
    }

    /**
     * Method to convert from base unit (m) to other units
     *
     * @param inUnit    Id of selected unit format
     * @param inValue   Base-value to convert
     */
    private calcLongitude(inUnit: number, inValue: number): number {
        let value = 0;
        switch (inUnit) {
            /** m */
            case 0:
                value = inValue;
                break;
            /** yd */
            case 1:
                value = (inValue * 1.0936);
                break;
            default:
                break;
        }
        return value;
    }

    /**
     * Method to convert from base unit (kg) to other units
     *
     * @param inUnit    Id of selected unit format
     * @param inValue   Base-value to convert
     */
    private calcMass(inUnit: number, inValue: number): number {
        let value = 0;
        switch (inUnit) {
            /** kg */
            case 0:
                value = inValue;
                break;
            /** lbs */
            case 1:
                value = (inValue * 2.2046226218);
                break;
            default:
                break;
        }
        return value;
    }

    /**
     * Method to convert from base unit (cN/tex) to other units
     *
     * @param inUnit    Id of selected unit format
     * @param inValue   Base-value to convert
     */
    private calcStrengthType(inUnit: number, inValue: number): number {
        let value = 0;
        switch (inUnit) {
            /** cN/tex */
            case 0:
                value = inValue;
                break;
            /**
             * CLSP
             * 1RKm=CLSP/156,4 --> RKm * 156.4 = CLSP
             * --> (cN/tex * 1.0194) * 156.4 = CLSP
             */
            case 1:
                value = (inValue * 1.0194) * 156.4;
                break;
            /**
             * RKM
             * 1cN/tex=0,981RKm --> RKm = cN/tex * 1.0194
             */
            case 2:
                value = (inValue * 1.0194);
                break;
            default:
                break;
        }
        return value;
    }
}

export default UnitsHelperUtil;
